import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Users, Sparkles, ShieldCheck, Clock,
  AlertTriangle, Crown, Search, Download, Mail, CheckCircle2, XCircle
} from "lucide-react";

const BUCKET_META = {
  all:        { label: "Tots",         color: "from-slate-700 to-slate-800",  text: "text-slate-200", icon: Users },
  registered: { label: "Registrats",   color: "from-blue-900 to-slate-900",   text: "text-blue-200",  icon: Users },
  trial:      { label: "En prova (7d)", color: "from-purple-900 to-slate-900", text: "text-purple-200", icon: Clock },
  active:     { label: "Actius",       color: "from-teal-900 to-emerald-900", text: "text-teal-200",  icon: ShieldCheck },
  canceled:   { label: "Cancel·lats",  color: "from-amber-900 to-slate-900",  text: "text-amber-200", icon: Crown },
  expired:    { label: "Expirats",     color: "from-rose-900 to-slate-900",   text: "text-rose-200",  icon: AlertTriangle },
  beta:       { label: "Beta",         color: "from-pink-900 to-slate-900",   text: "text-pink-200",  icon: Sparkles },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ca-ES", {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ label, value, hint, gradient, accent, Icon, onClick, active }) => (
  <button
    data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    onClick={onClick}
    className={`text-left w-full rounded-2xl p-5 border transition-all bg-gradient-to-br ${gradient}
      ${active ? "border-teal-400 ring-2 ring-teal-400/40" : "border-white/10 hover:border-white/30"}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className={`text-xs font-medium uppercase tracking-wider ${accent}`}>{label}</p>
        <p className="text-3xl font-bold text-white mt-2">{value}</p>
        {hint && <p className="text-xs text-slate-400 mt-2">{hint}</p>}
      </div>
      {Icon && <Icon className={`w-6 h-6 ${accent}`} />}
    </div>
  </button>
);

const AdminPanel = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeBucket, setActiveBucket] = useState("all");
  const [query, setQuery] = useState("");
  const [includeInternal, setIncludeInternal] = useState(false);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_internal: includeInternal }
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accés denegat. Només l'administrador pot veure aquest panell.");
      } else {
        setError("Error carregant el panell.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [includeInternal]);

  const allUsers = useMemo(() => {
    if (!data?.buckets) return [];
    return [
      ...data.buckets.registered,
      ...data.buckets.trial,
      ...data.buckets.active,
      ...data.buckets.canceled,
      ...data.buckets.expired,
      ...data.buckets.beta,
    ];
  }, [data]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    const list = activeBucket === "all" ? allUsers : (data.buckets?.[activeBucket] || []);
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(u =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    );
  }, [data, allUsers, activeBucket, query]);

  const exportCSV = () => {
    if (!filteredUsers.length) return;
    const headers = ["Email", "Nom", "Idioma", "Estat", "Verificat", "Creat", "Final subscripció"];
    const rows = filteredUsers.map(u => [
      u.email, u.name, u.language, u.bucket,
      u.email_verified ? "sí" : "no",
      u.created_at || "",
      u.subscription_end_date || ""
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `essencia-users-${activeBucket}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center">
        <div className="animate-pulse text-teal-300">Carregant panell d'administració…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex flex-col items-center justify-center px-4 text-center">
        <ShieldCheck className="w-12 h-12 text-rose-400 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Accés restringit</h1>
        <p className="text-slate-300 mb-6">{error}</p>
        <button
          data-testid="admin-back-btn"
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg"
        >
          Tornar al Dashboard
        </button>
      </div>
    );
  }

  const stats = data?.stats || {};
  const today = data?.today || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 pb-24" data-testid="admin-panel-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-back-btn"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Dashboard</span>
            </button>
            <div className="h-6 w-px bg-slate-700" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Panell d'administració
              </h1>
              <p className="text-sm text-slate-400 mt-1">Hola {user?.name} · {user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="admin-toggle-internal"
              onClick={() => setIncludeInternal(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                includeInternal
                  ? "bg-purple-700/40 hover:bg-purple-600/40 text-purple-100 border-purple-500/40"
                  : "bg-slate-800/70 hover:bg-slate-700 text-slate-300 border-slate-700"
              }`}
              title={includeInternal ? "Amagar comptes interns" : "Mostrar comptes interns"}
            >
              {includeInternal ? "Amagar interns" : "Mostrar interns"}
              {stats.internal_excluded > 0 && (
                <span className="text-[10px] opacity-70">({stats.internal_excluded})</span>
              )}
            </button>
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchDashboard}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800/70 hover:bg-slate-700 text-slate-200 rounded-lg border border-slate-700 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Actualitzar
            </button>
            <button
              data-testid="admin-export-csv-btn"
              onClick={exportCSV}
              className="flex items-center gap-2 px-3 py-2 bg-teal-700/80 hover:bg-teal-600 text-white rounded-lg text-sm"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard
            label="Total" value={stats.total_users || 0}
            gradient={BUCKET_META.all.color} accent={BUCKET_META.all.text} Icon={Users}
            onClick={() => setActiveBucket("all")} active={activeBucket === "all"}
            hint={`+${stats.recent_7d || 0} aquesta setmana`}
          />
          <StatCard
            label="Registrats" value={stats.registered || 0}
            gradient={BUCKET_META.registered.color} accent={BUCKET_META.registered.text} Icon={Users}
            onClick={() => setActiveBucket("registered")} active={activeBucket === "registered"}
            hint="Sense iniciar prova"
          />
          <StatCard
            label="En prova" value={stats.trial || 0}
            gradient={BUCKET_META.trial.color} accent={BUCKET_META.trial.text} Icon={Clock}
            onClick={() => setActiveBucket("trial")} active={activeBucket === "trial"}
            hint="7 dies gratuïts"
          />
          <StatCard
            label="Actius" value={stats.active || 0}
            gradient={BUCKET_META.active.color} accent={BUCKET_META.active.text} Icon={ShieldCheck}
            onClick={() => setActiveBucket("active")} active={activeBucket === "active"}
            hint="Subscripció pagant"
          />
          <StatCard
            label="Expirats" value={stats.expired || 0}
            gradient={BUCKET_META.expired.color} accent={BUCKET_META.expired.text} Icon={AlertTriangle}
            onClick={() => setActiveBucket("expired")} active={activeBucket === "expired"}
            hint="Prova caducada"
          />
          <StatCard
            label="Early Bird" value={stats.early_bird_remaining ?? 0}
            gradient="from-pink-900 to-purple-900" accent="text-pink-200" Icon={Crown}
            onClick={() => setActiveBucket("active")} active={false}
            hint={`/${stats.early_bird_limit || 100} disponibles`}
          />
        </div>

        {/* Today snapshot */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="rounded-xl p-4 bg-slate-900/60 border border-slate-800">
            <p className="text-xs uppercase text-slate-400">Sentir avui</p>
            <p className="text-2xl font-bold text-teal-300 mt-1">{today.feelings || 0}</p>
          </div>
          <div className="rounded-xl p-4 bg-slate-900/60 border border-slate-800">
            <p className="text-xs uppercase text-slate-400">Diari avui</p>
            <p className="text-2xl font-bold text-purple-300 mt-1">{today.journal_entries || 0}</p>
          </div>
          <div className="rounded-xl p-4 bg-slate-900/60 border border-slate-800">
            <p className="text-xs uppercase text-slate-400">Xats avui</p>
            <p className="text-2xl font-bold text-pink-300 mt-1">{today.chats || 0}</p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {["all", "registered", "trial", "active", "canceled", "expired", "beta"].map(k => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setActiveBucket(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                ${activeBucket === k
                  ? "bg-teal-600 text-white"
                  : "bg-slate-800/60 text-slate-300 hover:bg-slate-700"}`}
            >
              {BUCKET_META[k]?.label || k}
              <span className="ml-2 text-[10px] opacity-70">
                {k === "all" ? allUsers.length : (data?.buckets?.[k]?.length || 0)}
              </span>
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per email o nom…"
              className="pl-9 pr-3 py-2 bg-slate-900/70 border border-slate-700 text-slate-200 placeholder-slate-500 rounded-lg text-sm w-64 focus:outline-none focus:border-teal-500"
            />
          </div>
        </div>

        {/* Users table */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-slate-900/80 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Usuari</th>
                  <th className="text-left px-4 py-3">Estat</th>
                  <th className="text-left px-4 py-3">Idioma</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Email verificat</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Registrat</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Final subscripció</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr><td colSpan="6" className="text-center text-slate-500 py-10">Cap usuari en aquest filtre.</td></tr>
                )}
                {filteredUsers.map(u => {
                  const meta = BUCKET_META[u.bucket] || BUCKET_META.registered;
                  return (
                    <tr key={u.id} className="border-t border-slate-800/70 hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-600 to-purple-700 flex items-center justify-center text-white text-xs font-bold">
                            {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-white font-medium">{u.name || "—"}</div>
                            <div className="text-slate-400 text-xs flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${meta.text} bg-slate-800/80`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 uppercase text-xs">{u.language || "—"}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {u.email_verified
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          : <XCircle className="w-4 h-4 text-rose-400" />}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-300">{formatDate(u.created_at)}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-300">{formatDate(u.subscription_end_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
